import { createContext, useContext, type ComponentType, type ReactNode } from 'react'

/**
 * The one seam that lets a single chrome serve two bundles.
 *
 * fuguelab.com is served by TWO independent Vite builds behind one CloudFront
 * distribution: `studio/client` owns `/`, and `studio/hymnal` owns `/hymnal/*`
 * (plan D9). They share a domain, a bucket and a design system, but NOT a
 * router — each has its own, and neither knows the other's routes.
 *
 * That is the whole reason `Link` is injected rather than imported. A
 * react-router `<Link to="/services">` rendered inside the hymnal would be
 * intercepted by the HYMNAL's router, match nothing, and land the visitor on a
 * client-side 404 without ever hitting the network — the marketing site is
 * right there at that URL, but only a real navigation reaches it. So studio
 * passes a router Link (client-side, no reload, correct) and the hymnal passes
 * a plain `<a>` (a real navigation across the bundler boundary, also correct).
 * Neither is a fallback for the other.
 */

/** Props every internal-route link adapter must accept. */
export interface ChromeLinkProps {
  href: string
  className?: string
  onClick?: () => void
  /** Accessible name when the link's only content is an icon. */
  'aria-label'?: string
  children: ReactNode
}

/** Everything the chrome cannot know about the app hosting it. */
export interface ChromeConfig {
  /**
   * Renders a link to an internal route. Studio wraps react-router's `<Link>`;
   * the hymnal returns a plain `<a>`. See the note above for why this is not a
   * detail either side may choose freely.
   */
  Link: ComponentType<ChromeLinkProps>
  /**
   * Newsletter signup, or `null` to omit the form entirely.
   *
   * The endpoint is INJECTED rather than read from `import.meta.env` inside
   * this package, because Vite inlines env vars at build time per app: a
   * `VITE_*` read in shared source resolves against whichever app is building,
   * so the same code would silently point at different endpoints — or at
   * `undefined` in the app that forgot to declare the var, with no error until
   * a real visitor submitted the form.
   */
  newsletter: NewsletterConfig | null
}

/**
 * The props the newsletter form passes to whatever button it is given.
 *
 * `type` spans the full `<button>` union even though the form only ever passes
 * `'submit'`: React's legacy `propTypes` field on a `forwardRef` component is
 * invariant, so narrowing it here makes studio's own `<Button>` unassignable.
 */
export interface NewsletterButtonProps {
  type?: 'button' | 'submit' | 'reset'
  disabled?: boolean
  children: ReactNode
}

export interface NewsletterConfig {
  /** Absolute URL that accepts `POST {email, name?}`. */
  endpoint: string
  /**
   * Submit button. Injected so studio keeps rendering its own `<Button>`
   * (identical markup, `data-slot` and variants included) instead of a
   * lookalike that would drift from the design system.
   *
   * Typed as accepting AT LEAST these props rather than exactly them: studio's
   * `Button` is a forwardRef with the full `<button>` surface, and an exact
   * type would reject it for being too permissive.
   */
  Button: ComponentType<NewsletterButtonProps>
  /**
   * Where success and failure messages go. Studio passes `useFeedback().toast`;
   * an app with no toast host can pass a no-op and rely on the inline
   * confirmation the form renders on success either way.
   */
  notify: (message: string, tone: 'success' | 'error') => void
}

const ChromeConfigContext = createContext<ChromeConfig | null>(null)

export function ChromeConfigProvider({ config, children }: { config: ChromeConfig; children: ReactNode }) {
  return <ChromeConfigContext.Provider value={config}>{children}</ChromeConfigContext.Provider>
}

// eslint-disable-next-line react-refresh/only-export-components -- hook is colocated with its provider by convention
export function useChromeConfig(): ChromeConfig {
  const ctx = useContext(ChromeConfigContext)
  if (!ctx) {
    throw new Error('@gjl/studio-chrome components must be rendered inside <ChromeConfigProvider>')
  }
  return ctx
}

/** Internal-route link. Delegates to the adapter the host app supplied. */
export function ChromeLink(props: ChromeLinkProps) {
  const { Link } = useChromeConfig()
  return <Link {...props} />
}
